/**
 * Simple client-side router for FireGuard SPA
 * Handles navigation without full page reloads
 */

type RouteHandler = () => void | Promise<void>;

interface Route {
  path: string;
  handler: RouteHandler;
  requiresAuth?: boolean;
}

class Router {
  private routes: Route[] = [];
  private notFoundHandler?: RouteHandler;
  private currentPath: string = '';

  constructor() {
    // Listen for browser back/forward buttons
    window.addEventListener('popstate', () => {
      this.handleRoute(window.location.pathname);
    });

    // Intercept all link clicks
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;

      if (link && this.isInternalLink(link)) {
        e.preventDefault();
        this.navigate(link.getAttribute('href')!);
      }
    });
  }

  /**
   * Check if link is internal (same origin)
   */
  private isInternalLink(link: HTMLAnchorElement): boolean {
    return (
      link.origin === window.location.origin &&
      !link.hasAttribute('data-external') &&
      !link.getAttribute('href')?.startsWith('#')
    );
  }

  /**
   * Register a route
   */
  on(path: string, handler: RouteHandler, requiresAuth = false): void {
    this.routes.push({ path, handler, requiresAuth });
  }

  /**
   * Register 404 handler
   */
  notFound(handler: RouteHandler): void {
    this.notFoundHandler = handler;
  }

  /**
   * Navigate to a path
   */
  navigate(path: string): void {
    if (path === this.currentPath) return;

    window.history.pushState({}, '', path);
    this.handleRoute(path);
  }

  /** Alias of navigate(); several pages use the history-style name. */
  push(path: string): void {
    this.navigate(path);
  }

  /**
   * Replace current history entry
   */
  replace(path: string): void {
    window.history.replaceState({}, '', path);
    this.handleRoute(path);
  }

  /**
   * Go back in history
   */
  back(): void {
    window.history.back();
  }

  /**
   * Handle route change
   */
  private async handleRoute(path: string): Promise<void> {
    this.currentPath = path;

    // Find matching route
    const route = this.routes.find((r) => this.matchPath(r.path, path));

    if (!route) {
      // 404
      if (this.notFoundHandler) {
        await this.notFoundHandler();
      }
      return;
    }

    // Check authentication
    if (route.requiresAuth && !this.isAuthenticated()) {
      this.navigate('/login');
      return;
    }

    // Execute route handler
    try {
      await route.handler();
    } catch (error) {
      console.error('Route handler error:', error);
    }
  }

  /**
   * Match route path (supports exact match only for now)
   * Can be extended to support params like /extinguishers/:id
   */
  private matchPath(routePath: string, actualPath: string): boolean {
    // Exact match
    if (routePath === actualPath) return true;

    // Support wildcards
    if (routePath.endsWith('*')) {
      const prefix = routePath.slice(0, -1);
      return actualPath.startsWith(prefix);
    }

    // Support path parameters
    const routeParts = routePath.split('/');
    const pathParts = actualPath.split('/');

    if (routeParts.length !== pathParts.length) return false;

    return routeParts.every((part, i) => {
      return part.startsWith(':') || part === pathParts[i];
    });
  }

  /**
   * Get path parameter value
   */
  getParam(name: string): string | null {
    const route = this.routes.find((r) => this.matchPath(r.path, this.currentPath));
    if (!route) return null;

    const routeParts = route.path.split('/');
    const pathParts = this.currentPath.split('/');

    const paramIndex = routeParts.findIndex((part) => part === `:${name}`);
    if (paramIndex === -1) return null;

    return pathParts[paramIndex] || null;
  }

  /**
   * Check if user is authenticated
   * Override this method or use a callback
   */
  private isAuthenticated(): boolean {
    // Check if token exists in localStorage
    return !!localStorage.getItem('auth_token');
  }

  /**
   * Start the router (call once on app init)
   */
  start(): void {
    this.handleRoute(window.location.pathname);
  }

  /**
   * Get current path
   */
  getCurrentPath(): string {
    return this.currentPath;
  }
}

// Export singleton instance
export const router = new Router();
